import React, { useEffect, useState } from "react";
import { useQuery, gql } from "@apollo/client";
import { Button, DialogContent, TextField, List, ListItem, Grid } from "@material-ui/core";
import { Link } from "@reach/router";
import { getFormNamesByQuestion } from "types/gql-types";
import { Modal } from "components/haven-design";

const GET_FORM_NAMES_BY_QUESTION = gql`
  query getFormNamesByQuestion($questionId: ID!) {
    formsByQuestion(questionId: $questionId) {
      formDraftId
      name
    }
  }
`;

interface FormListProps {
  forms: { formDraftId: string; name: string; count: number }[];
}

const FormList: React.FC<FormListProps> = ({ forms }) => (
  <List>
    {forms.map((form, index) => (
      <ListItem key={index}>
        <Link to={`/dash/admin/forms/${form.formDraftId}`}>
          {form.name}
        </Link>
        &nbsp;{form.count > 1 && ` (${form.count})`}
      </ListItem>
    ))}
  </List>
);

interface FilterSectionProps {
  filter: string;
  handleFilterChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  clearFilter: () => void;
}

const FilterSection: React.FC<FilterSectionProps> =
({ filter, handleFilterChange, clearFilter }) => (
  <Grid container spacing={1} alignItems="flex-end">
    <Grid item xs={10}>
      <TextField
        autoFocus
        margin="dense"
        label="Filter"
        placeholder="Form name"
        type="text"
        fullWidth
        value={filter}
        onChange={handleFilterChange}
      />
    </Grid>
    <Grid item xs={2}>
      <Button variant="outlined" color="secondary" onClick={clearFilter}>
    Clear
      </Button>
    </Grid>
  </Grid>
);

interface FormNamesModalProps {
  open: boolean;
  questionId: string | null;
  questionName: string | null;
  onClose: () => void;
}

const FormNamesModal: React.FC<FormNamesModalProps> =
({ open, questionId, questionName, onClose }) => {
  const [filter, setFilter] = useState<string>("");
  const { loading, error, data } = useQuery<getFormNamesByQuestion>(GET_FORM_NAMES_BY_QUESTION, {
    skip: !questionId,
    variables: { questionId },
  });

  const clearFilter = () => setFilter("");
  // Reset the filter whenever the questionId changes
  useEffect(() => clearFilter(), [questionId]);
  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => setFilter(e.target.value);

  if (loading) return <p>Loading...</p>;
  if (error) return <p>Error: {error.message}</p>;
  // Create a mapping to store unique formDraftId and their counts
  const formMap: { [key: string]: { name: string, count: number } } = {};

  data?.formsByQuestion.forEach(form => {
    if (formMap[form.formDraftId]) {
      formMap[form.formDraftId].count += 1;
    } else {
      formMap[form.formDraftId] = { name: form.name, count: 1 };
    }
  });

  const uniqueForms = Object.keys(formMap).map(id => ({ formDraftId: id, ...formMap[id] }));
  const filteredForms = uniqueForms.filter(form =>
    form.name.toLowerCase().includes(filter.toLowerCase()))
    .sort((a, b) => a.name.localeCompare(b.name));

  return (
    <Modal isOpen={open} onRequestClose={onClose}
      title={`Form Names for Question: ${questionName}`}>
      <DialogContent>
        <FilterSection
          filter={filter}
          handleFilterChange={handleFilterChange}
          clearFilter={clearFilter}
        />
        <FormList forms={filteredForms} />
      </DialogContent>
    </Modal>
  );
};

export default FormNamesModal;
